import { useReducer, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  APPROVAL_MODE_LABELS,
  ApprovalMode,
  PERMISSION_ACTION_LABELS,
  PermissionAction,
  PermissionEffect,
  PRINCIPAL_TYPE_LABELS,
  PrincipalType,
} from '@fleet/shared';
import { permissionsApi, type PermissionEntry } from '../../api/permissions';
import { vehicleGroupsApi } from '../../api/vehicle-groups';
import { AsyncBoundary } from '../../ui/AsyncBoundary';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { ErrorNotice } from '../../ui/ErrorNotice';
import { PrincipalPicker } from './PrincipalPicker';
import {
  draftReducer,
  entriesOf,
  fromServer,
  isDirty,
  keyOf,
  toPayload,
  type DraftEntry,
} from './draft';

export function PermissionEditorPage() {
  const { id } = useParams<{ id: string }>();

  const query = useQuery({
    queryKey: ['permissions', id],
    queryFn: () => permissionsApi.list(id!),
    enabled: Boolean(id),
    // Ein Fensterwechsel darf einen offenen Entwurf nicht unter der Hand
    // gegen frische Serverdaten austauschen.
    refetchOnWindowFocus: false,
  });

  return (
    <AsyncBoundary query={query}>
      {(entries) => <Editor key={id} groupId={id!} server={entries} />}
    </AsyncBoundary>
  );
}

function Editor({ groupId, server }: { groupId: string; server: PermissionEntry[] }) {
  const queryClient = useQueryClient();
  const [draft, dispatch] = useReducer(draftReducer, server, fromServer);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Für Name und Freigabemodus reicht die Liste — einen Detailendpunkt für
  // Fahrzeuggruppen gibt es nicht.
  const groups = useQuery({ queryKey: ['vehicle-groups'], queryFn: vehicleGroupsApi.list });
  const group = groups.data?.find((candidate) => candidate.id === groupId);

  const save = useMutation({
    mutationFn: () => permissionsApi.replace(groupId, toPayload(draft)),
    onSuccess: async (result) => {
      setSavedAt(Date.now());
      await queryClient.invalidateQueries({ queryKey: ['permissions', groupId] });
      // Berechtigungen entscheiden, welche Fahrzeuge buchbar erscheinen.
      await queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      await queryClient.invalidateQueries({ queryKey: ['vehicle-groups'] });
      await queryClient.invalidateQueries({ queryKey: ['availability'] });
      return result;
    },
  });

  const dirty = isDirty(draft, server);
  const bookEntries = entriesOf(draft, PermissionAction.BOOK);
  const approveEntries = entriesOf(draft, PermissionAction.APPROVE);

  /**
   * Sind *alle* Namen unaufgelöst, klemmt das Verzeichnis — dann gehört ein
   * Hinweis nach oben statt an jede einzelne Zeile.
   */
  const directoryDown = server.length > 0 && server.every((entry) => !entry.resolved);

  return (
    <div className="space-y-5">
      <div>
        <Link to="/admin/vehicle-groups" className="text-sm text-slate-500 hover:text-slate-900">
          ← Fahrzeuggruppen
        </Link>
        <h2 className="mt-1 text-base font-semibold">
          Berechtigungen{group ? `: ${group.name}` : ''}
        </h2>
        <p className="text-sm text-slate-500">
          <strong>Verbieten</strong> schlägt jedes <strong>Erlauben</strong> — so lässt sich einer
          einzelnen Person entziehen, was ihre Entra-Gruppe ihr gäbe. Gruppen werden transitiv
          aufgelöst, verschachtelte Mitgliedschaften zählen also mit.
        </p>
      </div>

      {directoryDown && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
          Kein einziger Name ließ sich im Verzeichnis auflösen — vermutlich ist Microsoft Graph
          gerade nicht erreichbar. Die angezeigten Namen stammen aus der gespeicherten Kopie. Die
          Berechtigungen selbst gelten unverändert weiter.
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <PermissionPanel
          action={PermissionAction.BOOK}
          entries={bookEntries}
          dispatch={dispatch}
          showResolvedWarning={!directoryDown}
          warning={
            bookEntries.length === 0
              ? 'Ohne Eintrag darf hier niemand buchen — ausgenommen Administration und Fuhrparkleitung.'
              : bookEntries.every((entry) => entry.effect === PermissionEffect.DENY)
                ? 'Diese Liste verweigert nur. Sie erlaubt niemandem etwas.'
                : undefined
          }
        />

        <PermissionPanel
          action={PermissionAction.APPROVE}
          entries={approveEntries}
          dispatch={dispatch}
          showResolvedWarning={!directoryDown}
          warning={
            approveEntries.length === 0 && group && group.approvalMode !== ApprovalMode.NONE
              ? `Diese Gruppe verlangt eine Freigabe (${APPROVAL_MODE_LABELS[group.approvalMode]}), aber niemand ist zum Freigeben berechtigt. Buchungen blieben liegen — außer Administration und Fuhrparkleitung greift ein.`
              : undefined
          }
        />
      </div>

      {save.isError && <ErrorNotice error={save.error} />}

      <div className="flex items-center justify-end gap-3">
        {savedAt && !dirty && <span className="text-sm text-emerald-700">Gespeichert.</span>}
        {dirty && <span className="text-sm text-amber-700">Ungespeicherte Änderungen</span>}
        <Button onClick={() => dispatch({ type: 'reset', entries: server })} disabled={!dirty}>
          Verwerfen
        </Button>
        <Button
          variant="primary"
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? 'Wird gespeichert …' : 'Speichern'}
        </Button>
      </div>
    </div>
  );
}

function PermissionPanel({
  action,
  entries,
  dispatch,
  warning,
  showResolvedWarning,
}: {
  action: PermissionAction;
  entries: DraftEntry[];
  dispatch: React.Dispatch<Parameters<typeof draftReducer>[1]>;
  warning?: string;
  showResolvedWarning: boolean;
}) {
  return (
    <section className="space-y-3 rounded-lg border border-slate-200 bg-white p-4">
      <h3 className="text-sm font-semibold">{PERMISSION_ACTION_LABELS[action]}</h3>

      {warning && (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {warning}
        </p>
      )}

      <ul className="divide-y divide-slate-100">
        {entries.map((entry) => {
          const key = keyOf(entry);
          const isDeny = entry.effect === PermissionEffect.DENY;

          return (
            <li key={key} className="flex items-center justify-between gap-3 py-2">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 truncate text-sm">
                  <span className="font-medium">{entry.displayName}</span>
                  {showResolvedWarning && !entry.resolved && (
                    <span
                      className="text-amber-600"
                      title="Im Verzeichnis nicht auffindbar — das Konto könnte gelöscht sein. Die Berechtigung gilt weiter."
                    >
                      ?
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500">
                  {PRINCIPAL_TYPE_LABELS[entry.principalType]}
                  {entry.principalType === PrincipalType.ENTRA_GROUP && ' · transitiv'}
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-1">
                <select
                  value={entry.effect}
                  onChange={(event) =>
                    dispatch({
                      type: 'setEffect',
                      key,
                      effect: event.target.value as PermissionEffect,
                    })
                  }
                  className={[
                    'rounded-md border px-2 py-1 text-xs',
                    isDeny ? 'border-red-300 bg-red-50 text-red-800' : 'border-slate-300',
                  ].join(' ')}
                  aria-label={`Wirkung für ${entry.displayName}`}
                >
                  <option value={PermissionEffect.ALLOW}>Erlauben</option>
                  <option value={PermissionEffect.DENY}>Verbieten</option>
                </select>
                <Button size="sm" variant="ghost" onClick={() => dispatch({ type: 'remove', key })}>
                  Entfernen
                </Button>
              </div>
            </li>
          );
        })}
      </ul>

      {entries.length === 0 && !warning && (
        <p className="text-xs text-slate-500">Noch keine Einträge.</p>
      )}

      <PrincipalPicker
        onSelect={(principal) => dispatch({ type: 'add', action, principal })}
      />
    </section>
  );
}
